import { AntragStammdaten } from '@/store/application/types';

/**
 * Metadaten der Personen-Stammdatenfelder für die Antrag-Detailseite (BER-67).
 * `typ` steuert das Eingabe-Widget; `optionen` liefert die Auswahl bei Selects.
 */
export type FeldTyp = 'text' | 'datum' | 'select' | 'janein' | 'alter';

export interface FeldOption {
  value: string;
  label: string;
}

export interface FeldMeta {
  key: keyof AntragStammdaten;
  label: string;
  typ: FeldTyp;
  span: number;
  optionen?: FeldOption[];
}

/**
 * Server-gelieferte Metadaten der dynamischen softfair-Katalogfelder
 * (Schlüssel `sf_…`, Admin-zuschaltbar): werden beim `resolve`-Aufruf
 * registriert, damit der Wizard sie wie feste Felder rendern kann.
 */
const dynamischeMeta: Record<string, FeldMeta> = {};

export const registriereFeldMeta = (
  felder: { value: string; label: string; typ?: string; optionen?: FeldOption[] }[],
): void => {
  felder.forEach((f) => {
    if (!f.typ) return; // feste Wizard-Felder haben lokale Meta (FELD_META).
    dynamischeMeta[f.value] = {
      key: f.value,
      label: f.label,
      typ: f.typ as FeldTyp,
      span: 6,
      optionen: f.optionen,
    };
  });
};

/**
 * Meta zu einem Feld-Schlüssel: fest (FELD_META) oder dynamisch (Server).
 * Nimmt string | number an, weil `keyof AntragStammdaten` durch die
 * Index-Signatur (dynamische sf_-Felder) auch number enthält.
 */
export const feldMeta = (key: string | number): FeldMeta => {
  const k = String(key);
  return FELD_META[k]
    ?? dynamischeMeta[k]
    ?? { key: k, label: k, typ: 'text', span: 6 };
};

// Anrede (bildet zugleich das Geschlecht ab — eigenes Geschlechtsfeld entfällt).
const ANREDE: FeldOption[] = [
  { value: 'Herr', label: 'Herr' },
  { value: 'Frau', label: 'Frau' },
];

const FAMILIENSTAND: FeldOption[] = [
  { value: 'single', label: 'Ledig' },
  { value: 'married', label: 'Verheiratet' },
  { value: 'divorced', label: 'Geschieden' },
  { value: 'widowed', label: 'Verwitwet' },
  { value: 'cohabiting', label: 'In Partnerschaft' },
  { value: 'single_parent', label: 'Alleinerziehend' },
];

/**
 * Berufsstatus (Beschäftigungsstatus) — Werte = CRM-EmploymentStatusEnum
 * (enumLabels.employment_status), damit Antrag und Kundenstammsatz identisch
 * sind. VN-Pflichtfeld; erscheint u. a. in der Beratungsdoku.
 */
const BERUFSSTATUS: FeldOption[] = [
  { value: 'ss_contributions', label: 'Angestellt (SV-pflichtig)' },
  { value: 'self_emp_no_emp', label: 'Selbstständig (ohne AN)' },
  { value: 'self_emp_with_emp', label: 'Selbstständig (mit AN)' },
  { value: 'emp_md_partner', label: 'GF/geschäftsf. Gesellschafter' },
  { value: 'mini_job', label: 'Minijob' },
  { value: 'job_seeker', label: 'Arbeitssuchend' },
  { value: 'housewife_husband', label: 'Hausfrau/-mann' },
  { value: 'disabled', label: 'Erwerbsgemindert' },
  { value: 'old_age_pensioner', label: 'Rentner/in' },
  { value: 'civil_servant', label: 'Beamte/r' },
  { value: 'public_sector_employee', label: 'Öffentlicher Dienst' },
];

/** Ausweistypen (AXA). */
export const AUSWEIS_TYPEN: FeldOption[] = [
  { value: 'personalausweis_de', label: 'Personalausweis (deutsch)' },
  { value: 'reisepass_de', label: 'Reisepass (deutsch)' },
  { value: 'sonstiges', label: 'Sonstiges Dokument' },
];

/**
 * Staaten der softfair-LV-API (Staat-Enum der Spec, ISO-2) — Basis des
 * Nationalitäten-Dropdowns. Es gibt keinen Endpoint dafür; die Liste ist in
 * der API fest und gilt für alle Gesellschaften und Tarife gleichermaßen.
 */
const SOFTFAIR_STAAT_CODES = (
  'AF,EG,AL,DZ,US,AD,AO,AG,GQ,AR,AM,AZ,ET,AU,BS,BH,BD,BB,BE,BZ,BJ,BT,BO,BA,BW,BR,GB,BN,BG,BF,BI,'
  + 'CL,CN,CR,DK,DE,DM,DO,DJ,EC,AE,ER,EE,FJ,FI,FR,GA,GM,GE,GH,GD,GR,GT,GW,GN,GY,HT,HN,HK,IN,ID,IQ,'
  + 'IR,IE,IS,IL,IT,CI,JM,JP,YE,JO,KH,CM,CA,CV,KZ,QA,KE,KG,KI,CO,KM,CD,CG,KP,KR,XK,HR,CU,KW,LA,LS,'
  + 'LV,LB,LR,LY,LI,LT,LC,LU,MG,MW,MY,MV,ML,MT,MA,MH,MR,MU,MK,MX,FM,MD,MC,MN,MO,ME,MZ,MM,NA,NR,NP,'
  + 'NZ,NI,NL,NG,NE,NU,NO,OM,AT,TL,PK,PS,PW,PA,PG,PY,PE,PH,PL,PT,RW,RO,RU,SB,SV,ZM,WS,SM,ST,SA,SE,'
  + 'CH,SN,RS,SC,SL,ZW,SG,SK,SI,SO,ES,LK,ZA,SS,SD,SR,SZ,SY,TJ,TW,TZ,TH,TG,TO,TD,CZ,TN,TR,TM,TV,UG,'
  + 'UA,HU,UY,UZ,VU,VA,VE,VN,VC,KN,TT,BY,CF,CY,GL,GI,IM,JE,GG,PR,RE,NC,PF,FO,KY,BM,AW,CW,MQ,GP'
).split(',');

/**
 * Nationalitäten-Optionen: deutsche Ländernamen via Intl, alphabetisch,
 * Deutschland zuerst. Codes ohne bekannten deutschen Namen fallen raus.
 */
export const NATIONALITAETEN: FeldOption[] = (() => {
  const namen = new Intl.DisplayNames(['de'], { type: 'region' });
  const optionen: FeldOption[] = [];
  SOFTFAIR_STAAT_CODES.forEach((code) => {
    if (code === 'DE') return;
    let label: string | undefined;
    try {
      label = namen.of(code);
    } catch {
      label = undefined;
    }
    if (label && label !== code) optionen.push({ value: code, label });
  });
  optionen.sort((a, b) => a.label.localeCompare(b.label, 'de'));
  return [{ value: 'DE', label: 'Deutschland' }, ...optionen];
})();

/** Gängige Freitext-Nationalitäten (Adjektivform) → ISO-Code des Dropdowns. */
const NATIONALITAET_SYNONYME: Record<string, string> = {
  deutsch: 'DE',
  österreichisch: 'AT',
  schweizerisch: 'CH',
  türkisch: 'TR',
  polnisch: 'PL',
  italienisch: 'IT',
  französisch: 'FR',
  spanisch: 'ES',
  portugiesisch: 'PT',
  niederländisch: 'NL',
  griechisch: 'GR',
  kroatisch: 'HR',
  rumänisch: 'RO',
  bulgarisch: 'BG',
  ukrainisch: 'UA',
  russisch: 'RU',
  britisch: 'GB',
  amerikanisch: 'US',
  syrisch: 'SY',
  afghanisch: 'AF',
};

/**
 * Bildet Alt-/Freitextwerte („deutsch", „Polen", „de") auf den ISO-Code des
 * Nationalitäten-Dropdowns ab; unbekannte Werte ergeben '' (leer = neu wählen).
 * Gespeicherte Anträge können `null` tragen (Sentry FE-P): das ergibt ebenfalls ''.
 */
export const nationalitaetAlsIso = (wert: string | null | undefined): string => {
  const eingabe = (wert ?? '').trim();
  if (eingabe === '') return '';
  if (/^[A-Za-z]{2}$/.test(eingabe)) return eingabe.toUpperCase();
  const klein = eingabe.toLowerCase();
  if (NATIONALITAET_SYNONYME[klein]) return NATIONALITAET_SYNONYME[klein];
  return NATIONALITAETEN.find((o) => o.label.toLowerCase() === klein)?.value ?? '';
};

/**
 * Alle bekannten Personenfelder mit ihren Metadaten. Der Schlüssel entspricht
 * dem VpFieldEnum-Wert im Backend, damit die Feld-Konfiguration darauf greift.
 */
export const FELD_META: Record<keyof AntragStammdaten, FeldMeta> = {
  first_name: { key: 'first_name', label: 'Vorname', typ: 'text', span: 6 },
  last_name: { key: 'last_name', label: 'Nachname', typ: 'text', span: 6 },
  date_of_birth: { key: 'date_of_birth', label: 'Geburtsdatum', typ: 'datum', span: 6 },
  salutation: { key: 'salutation', label: 'Anrede', typ: 'select', span: 6, optionen: ANREDE },
  nationality: { key: 'nationality', label: 'Nationalität', typ: 'select', span: 6, optionen: NATIONALITAETEN },
  marital_status: { key: 'marital_status', label: 'Familienstand', typ: 'select', span: 6, optionen: FAMILIENSTAND },
  // Beruf: kein VN-Standardfeld — je Gesellschaft/Typ im Admin zuschaltbar.
  job_title: { key: 'job_title', label: 'Beruf', typ: 'text', span: 6 },
  employment_status: { key: 'employment_status', label: 'Berufsstatus', typ: 'select', span: 6, optionen: BERUFSSTATUS },
  birthplace: { key: 'birthplace', label: 'Geburtsort', typ: 'text', span: 6 },
  // PDF-Nachbefüllung Allianz (keine API-Felder, Wizard-only).
  birth_country: { key: 'birth_country', label: 'Geburtsland', typ: 'text', span: 6 },
  birth_name: { key: 'birth_name', label: 'Geburtsname', typ: 'text', span: 6 },
  phone: { key: 'phone', label: 'Telefon', typ: 'text', span: 6 },
  email: { key: 'email', label: 'E-Mail', typ: 'text', span: 6 },
  address: { key: 'address', label: 'Straße und Hausnummer', typ: 'text', span: 12 },
  postal_code: { key: 'postal_code', label: 'PLZ', typ: 'text', span: 4 },
  city: { key: 'city', label: 'Ort', typ: 'text', span: 8 },
  tax_id: { key: 'tax_id', label: 'Steuer-ID', typ: 'text', span: 6 },
  // Riester (WWK-Blanko, 20.09.2026): admin-zuschaltbare Personenfelder.
  social_security_number: { key: 'social_security_number', label: 'Sozialversicherungsnummer', typ: 'text', span: 6 },
  gross_annual_income: { key: 'gross_annual_income', label: 'Bruttojahreseinkommen', typ: 'text', span: 6 },
  iban: { key: 'iban', label: 'IBAN', typ: 'text', span: 6 },
  id_document_type: { key: 'id_document_type', label: 'Ausweistyp', typ: 'select', span: 6, optionen: AUSWEIS_TYPEN },
  id_document_number: { key: 'id_document_number', label: 'Ausweisnummer', typ: 'text', span: 6 },
  id_document_issued_at: { key: 'id_document_issued_at', label: 'Ausgestellt am', typ: 'datum', span: 6 },
  id_document_authority: { key: 'id_document_authority', label: 'Ausstellende Behörde', typ: 'text', span: 6 },
  id_document_valid_until: { key: 'id_document_valid_until', label: 'Gültig bis', typ: 'datum', span: 6 },
  id_document_country: { key: 'id_document_country', label: 'Ausstellungsland', typ: 'text', span: 6 },
};

/**
 * VN-Felder in Anzeige-Reihenfolge. Standardsatz für den Versicherungsnehmer
 * (die vollständigen Personendaten laut AXA). IBAN gehört zum Vertrag, nicht
 * zur Person, und ist hier bewusst NICHT enthalten.
 */
export const VN_FELD_KEYS: (keyof AntragStammdaten)[] = [
  'salutation', 'first_name', 'last_name', 'nationality', 'date_of_birth',
  'marital_status', 'employment_status', 'phone', 'email', 'address', 'postal_code',
  'city', 'tax_id', 'birthplace',
];

/** Ausweisfelder (VN) — eigener Block auf der Antrag-Detailseite. */
export const AUSWEIS_FELD_KEYS: (keyof AntragStammdaten)[] = [
  'id_document_type', 'id_document_number', 'id_document_authority', 'id_document_issued_at',
  'id_document_valid_until',
  'id_document_country',
];

/** Feld-Schlüssel, die am Mandanten im CRM gespeichert werden dürfen. */
export const CRM_SPEICHERBARE_FELDER: (keyof AntragStammdaten)[] = [
  'first_name', 'last_name', 'email', 'phone', 'address', 'postal_code', 'city',
  'date_of_birth', 'iban', 'nationality', 'salutation', 'marital_status',
  'job_title', 'tax_id', 'birthplace',
  'id_document_type', 'id_document_number', 'id_document_authority', 'id_document_issued_at',
  'id_document_valid_until',
  'id_document_country',
  // Riester (WWK-Blanko, 20.09.2026).
  'social_security_number', 'gross_annual_income',
];

/**
 * Bezugsberechtigung/Erbe (AXA). Die Option „Versicherungsnehmer" ist nur
 * sinnvoll, wenn VN ≠ VP — sie wird im Wizard dynamisch ein-/ausgeblendet
 * (siehe StepAntragDetail), das Label bleibt schlicht „Versicherungsnehmer".
 */
export const ERBE_ARTEN: FeldOption[] = [
  { value: 'vn', label: 'Versicherungsnehmer' },
  { value: 'ehepartner', label: 'Ehepartner' },
  { value: 'kinder', label: 'Kinder' },
  { value: 'eltern', label: 'Eltern' },
  { value: 'andere', label: 'Eine andere Person' },
];

export const ERBE_ART_ANDERE = 'andere';
export const ERBE_ART_VN = 'vn';
